/**
 * K-means clustering — Lloyd's algorithm with greedy k-means++ seeding.
 *
 * Faithful port of scikit-learn's `sklearn.cluster.KMeans` (algorithm="lloyd"):
 *   - greedy k-means++ initialization with 2 + floor(log k) local trials
 *     (Arthur & Vassilvitskii 2007; sklearn `_kmeans_plusplus`, _kmeans.py l.180)
 *   - Lloyd expectation-maximization with strict-label and center-shift
 *     tolerance convergence (sklearn `_kmeans_single_lloyd`, _kmeans.py l.630)
 *   - `nInit` independent restarts, keeping the lowest-inertia result
 *   - dataset-scaled tolerance `mean(var(X, axis=0)) * tol` (sklearn `_tolerance`)
 *
 * Determinism is total: the run is driven by a seeded mulberry32 PRNG. With a
 * fixed `seed` the labels, centers and inertia are reproducible bit-for-bit;
 * there is deliberately NO `Date.now()`/`Math.random()` fallback.
 *
 * Validated against committed reference fixtures
 * (three separable blobs, k=3, generated by sklearn.cluster.KMeans).
 *
 * @param {Array<ArrayLike<number>>} X2d
 *   Observations, shape (nSamples, nFeatures). Each row is a plain array or a
 *   typed array; all rows must share the same length.
 * @param {number} k - number of clusters (1 ≤ k ≤ nSamples).
 * @param {Object} [options]
 * @param {number} [options.nInit=10] - number of k-means++ restarts (≥ 1).
 * @param {number} [options.maxIter=300] - max Lloyd iterations per restart (≥ 1).
 * @param {number} [options.seed=0] - PRNG seed for reproducible seeding.
 * @param {number} [options.tol=1e-4] - relative center-shift tolerance
 *   (scaled by the mean feature variance, matching sklearn).
 * @returns {{ labels: Int32Array, centers: number[][], inertia: number }}
 *   `labels[i]` is the cluster index of observation i, `centers[c]` is the
 *   centroid of cluster c, and `inertia` is the summed squared distance of
 *   every observation to its assigned centroid.
 */
export function kmeans(X2d, k, { nInit = 10, maxIter = 300, seed = 0, tol = 1e-4 } = {}) {
  if (!Array.isArray(X2d) || X2d.length === 0 || X2d[0] == null || X2d[0].length == null) {
    throw new Error('kmeans: X2d must be a non-empty 2D array of shape (nSamples, nFeatures)')
  }
  const n = X2d.length
  const d = X2d[0].length
  if (d === 0) {
    throw new Error('kmeans: X2d rows must have at least one feature')
  }
  if (!Number.isInteger(k) || k < 1) {
    throw new Error(`kmeans: k=${k} must be a positive integer`)
  }
  if (k > n) {
    throw new Error(`kmeans: k=${k} cannot exceed nSamples=${n}`)
  }
  if (!Number.isInteger(nInit) || nInit < 1) {
    throw new Error(`kmeans: nInit=${nInit} must be a positive integer`)
  }
  if (!Number.isInteger(maxIter) || maxIter < 1) {
    throw new Error(`kmeans: maxIter=${maxIter} must be a positive integer`)
  }

  // Flatten to a dense row-major Float64Array; validate shape and finiteness.
  const data = new Float64Array(n * d)
  for (let i = 0; i < n; i++) {
    const row = X2d[i]
    if (row == null || row.length !== d) {
      throw new Error(`kmeans: row ${i} has length ${row == null ? 'undefined' : row.length}, expected ${d}`)
    }
    for (let j = 0; j < d; j++) {
      const v = row[j]
      if (!Number.isFinite(v)) {
        throw new Error(`kmeans: X2d[${i}][${j}] is non-finite (${v}); input must be all finite`)
      }
      data[i * d + j] = v
    }
  }

  const relTol = _tolerance(data, n, d, tol)
  const rng = mulberry32(seed)

  let best = null
  for (let init = 0; init < nInit; init++) {
    const centers = kmeansPlusPlus(data, n, d, k, rng)
    const run = lloyd(data, n, d, k, centers, maxIter, relTol)
    if (best === null || run.inertia < best.inertia) {
      best = run
    }
  }

  // Reshape flat centers into nested rows for a friendlier public shape.
  const centersOut = []
  for (let c = 0; c < k; c++) {
    const row = new Array(d)
    for (let j = 0; j < d; j++) row[j] = best.centers[c * d + j]
    centersOut.push(row)
  }

  return { labels: best.labels, centers: centersOut, inertia: best.inertia }
}

/**
 * Dataset-scaled convergence tolerance: `mean(var(X, axis=0)) * tol`.
 * Mirrors sklearn `_tolerance` (population variance, ddof=0). @private
 */
function _tolerance(data, n, d, tol) {
  if (tol === 0) return 0
  let meanVar = 0
  for (let j = 0; j < d; j++) {
    let mean = 0
    for (let i = 0; i < n; i++) mean += data[i * d + j]
    mean /= n
    let variance = 0
    for (let i = 0; i < n; i++) {
      const dv = data[i * d + j] - mean
      variance += dv * dv
    }
    meanVar += variance / n
  }
  return (meanVar / d) * tol
}

/**
 * Greedy k-means++ seeding (sklearn `_kmeans_plusplus`).
 * Returns a flat Float64Array of k*d initial centers. @private
 */
function kmeansPlusPlus(data, n, d, k, rng) {
  const centers = new Float64Array(k * d)
  const nLocalTrials = 2 + Math.floor(Math.log(k))

  // First center: uniform over samples (sample_weight is uniform).
  let centerId = Math.min(n - 1, Math.floor(rng() * n))
  for (let j = 0; j < d; j++) centers[j] = data[centerId * d + j]

  // closestDistSq[i] = squared distance of point i to the nearest chosen center.
  const closestDistSq = new Float64Array(n)
  let currentPot = 0
  for (let i = 0; i < n; i++) {
    const ds = sqDistPoint(data, i, centers, 0, d)
    closestDistSq[i] = ds
    currentPot += ds
  }

  // Running cumulative sum of closestDistSq, kept in sync for searchsorted.
  const cumSum = new Float64Array(n)
  const candidateDist = new Float64Array(n)
  const bestCandidateDist = new Float64Array(n)

  for (let c = 1; c < k; c++) {
    // cumsum of the current closest distances.
    let acc = 0
    for (let i = 0; i < n; i++) {
      acc += closestDistSq[i]
      cumSum[i] = acc
    }

    let bestCandidate = -1
    let bestPot = Infinity
    for (let t = 0; t < nLocalTrials; t++) {
      const randVal = rng() * currentPot
      let candidateId = searchSortedLeft(cumSum, n, randVal)
      if (candidateId > n - 1) candidateId = n - 1

      // Potential if candidateId became a center: sum of min(closest, dist).
      let pot = 0
      for (let i = 0; i < n; i++) {
        const dsc = sqDistBetween(data, i, candidateId, d)
        const dmin = dsc < closestDistSq[i] ? dsc : closestDistSq[i]
        candidateDist[i] = dmin
        pot += dmin
      }
      if (pot < bestPot) {
        bestPot = pot
        bestCandidate = candidateId
        bestCandidateDist.set(candidateDist)
      }
    }

    for (let j = 0; j < d; j++) centers[c * d + j] = data[bestCandidate * d + j]
    closestDistSq.set(bestCandidateDist)
    currentPot = bestPot
  }

  return centers
}

/**
 * One Lloyd run from fixed initial centers (sklearn `_kmeans_single_lloyd`).
 * @private
 */
function lloyd(data, n, d, k, centersInit, maxIter, tol) {
  let centers = Float64Array.from(centersInit)
  let centersNew = new Float64Array(k * d)
  const labels = new Int32Array(n).fill(-1)
  const labelsOld = new Int32Array(n).fill(-1)
  const counts = new Int32Array(k)

  let strictConvergence = false
  for (let iter = 0; iter < maxIter; iter++) {
    assignLabels(data, n, d, k, centers, labels)
    const shiftTot = updateCenters(data, n, d, k, centers, centersNew, labels, counts)

    // Swap: centers <- freshly computed centroids.
    const tmp = centers
    centers = centersNew
    centersNew = tmp

    if (arraysEqual(labels, labelsOld, n)) {
      strictConvergence = true
      break
    }
    if (shiftTot <= tol) break
    labelsOld.set(labels)
  }

  // Re-run the assignment so returned labels match the final centers exactly.
  if (!strictConvergence) {
    assignLabels(data, n, d, k, centers, labels)
  }

  let inertia = 0
  for (let i = 0; i < n; i++) {
    inertia += sqDistPoint(data, i, centers, labels[i], d)
  }

  return { labels: Int32Array.from(labels), centers, inertia }
}

/** Assign each point to its nearest center by squared distance. @private */
function assignLabels(data, n, d, k, centers, labels) {
  for (let i = 0; i < n; i++) {
    let bestC = 0
    let bestDist = Infinity
    for (let c = 0; c < k; c++) {
      const ds = sqDistPoint(data, i, centers, c, d)
      if (ds < bestDist) {
        bestDist = ds
        bestC = c
      }
    }
    labels[i] = bestC
  }
}

/**
 * Recompute centroids as the mean of assigned points, relocating any empty
 * cluster onto the point farthest from its current centroid (sklearn's
 * `relocate_empty_clusters` behavior). Returns the summed squared center shift.
 * @private
 */
function updateCenters(data, n, d, k, centers, centersNew, labels, counts) {
  centersNew.fill(0)
  counts.fill(0)
  for (let i = 0; i < n; i++) {
    const c = labels[i]
    counts[c]++
    const base = c * d
    for (let j = 0; j < d; j++) centersNew[base + j] += data[i * d + j]
  }
  for (let c = 0; c < k; c++) {
    if (counts[c] > 0) {
      const base = c * d
      for (let j = 0; j < d; j++) centersNew[base + j] /= counts[c]
    }
  }

  // Relocate empty clusters onto the worst-fit points (deterministic).
  const empties = []
  for (let c = 0; c < k; c++) if (counts[c] === 0) empties.push(c)
  if (empties.length > 0) {
    // Distance of each point to its currently-assigned new center.
    const order = Array.from({ length: n }, (_, i) => i).sort(
      (a, b) => sqDistPoint(data, b, centersNew, labels[b], d) - sqDistPoint(data, a, centersNew, labels[a], d),
    )
    for (let e = 0; e < empties.length; e++) {
      const c = empties[e]
      const p = order[e]
      const from = labels[p]
      const base = c * d
      for (let j = 0; j < d; j++) centersNew[base + j] = data[p * d + j]
      // Remove p from its old cluster's mean so it is not double-counted.
      if (counts[from] > 1) {
        const fbase = from * d
        for (let j = 0; j < d; j++) {
          centersNew[fbase + j] = (centersNew[fbase + j] * counts[from] - data[p * d + j]) / (counts[from] - 1)
        }
      }
      counts[from]--
      counts[c] = 1
      labels[p] = c
    }
  }

  let shiftTot = 0
  for (let c = 0; c < k; c++) {
    let s = 0
    const base = c * d
    for (let j = 0; j < d; j++) {
      const dv = centersNew[base + j] - centers[base + j]
      s += dv * dv
    }
    shiftTot += s
  }
  return shiftTot
}

/** Squared Euclidean distance between point i and center c (both flat). @private */
function sqDistPoint(data, i, centers, c, d) {
  let s = 0
  const pi = i * d
  const pc = c * d
  for (let j = 0; j < d; j++) {
    const dv = data[pi + j] - centers[pc + j]
    s += dv * dv
  }
  return s
}

/** Squared Euclidean distance between two data points i and c. @private */
function sqDistBetween(data, i, c, d) {
  let s = 0
  const pi = i * d
  const pc = c * d
  for (let j = 0; j < d; j++) {
    const dv = data[pi + j] - data[pc + j]
    s += dv * dv
  }
  return s
}

/** np.searchsorted(a, v, side='left'): first index with a[idx] >= v. @private */
function searchSortedLeft(a, n, v) {
  let lo = 0
  let hi = n
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (a[mid] < v) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** Int32Array equality over the first n entries. @private */
function arraysEqual(a, b, n) {
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) return false
  return true
}

/**
 * mulberry32 — a fast, seedable 32-bit PRNG yielding uniform [0, 1) doubles.
 * Deterministic given the seed; used so every kmeans run is reproducible.
 * @private
 */
function mulberry32(seed) {
  let a = seed >>> 0
  return function () {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
